import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { maybeParseCalendarDate, today } from '@rematter/common/dateTime';
import { ensureValue } from '@rematter/ui-web';
import { Button, DatePicker, Popover, TextField } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import * as state from '../../_state/loadDetails.state';
import { useTranslations } from '../../i18n/useTranslations';

export type MarkLoadAsReviewedPopoverProps = {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  loadId?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export const MarkLoadAsReviewedPopover = ({
  isOpen,
  loadId,
  triggerRef,
  onOpenChange,
  onSuccess,
}: MarkLoadAsReviewedPopoverProps) => {
  const t = useTranslations();
  const [millReceivedWeightPostDate, setMillReceivedWeightPostDate] =
    React.useState<CalendarDate | null>(today());

  const [receivedTicketIssueDate, setReceivedTicketIssueDate] =
    React.useState<CalendarDate | null>(today());

  const [receivingTicketNumber, setReceivingTicketNumber] =
    React.useState<string>('');

  const { data: loadDetails } = useAtomValue(state.$loadDetails);

  const [
    { isSuccess: didMarkAsReviewed, isPending: isMarkingAsReviewed },
    markLoadAsReviewed,
  ] = useAtom(state.$markLoadAsReviewed);

  const allowSubmitting = Boolean(
    receivingTicketNumber &&
    millReceivedWeightPostDate &&
    receivedTicketIssueDate,
  );

  const handleMarkAsReviewed = () => {
    if (!millReceivedWeightPostDate || !receivedTicketIssueDate) {
      return;
    }

    markLoadAsReviewed({
      loadId: ensureValue(loadId),
      millReceivedWeightPostDate: millReceivedWeightPostDate.toString(),
      receivedTicketIssueDate: receivedTicketIssueDate.toString(),
      receivingTicketNumber,
    });
  };

  React.useEffect(() => {
    if (didMarkAsReviewed) {
      onOpenChange(false);
      onSuccess();
    }
  }, [didMarkAsReviewed, onOpenChange, onSuccess]);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      triggerRef={triggerRef}
      placement="bottom right"
      className="@MarkLoadAsReviewedPopover"
    >
      <div className="grid grid-cols-2 gap-2 p-4">
        <DatePicker
          label={t.markLoadAsReviewed.loadReceivedOn}
          isRequired
          value={maybeParseCalendarDate(loadDetails?.loadReceivedDate)}
          isDisabled
        />
        <TextField
          label={t.markLoadAsReviewed.receivingTicketNumber}
          value={receivingTicketNumber}
          onChange={(x) => setReceivingTicketNumber(x)}
          autoFocus
        />
        <DatePicker
          label={t.markLoadAsReviewed.receivedTicketIssueDate}
          isRequired
          value={receivedTicketIssueDate}
          onChange={(x) => setReceivedTicketIssueDate(x)}
        />
        <DatePicker
          label={t.markLoadAsReviewed.millReceivedWeightPostDate}
          isRequired
          value={millReceivedWeightPostDate}
          onChange={(x) => setMillReceivedWeightPostDate(x)}
        />
        <div className="col-span-2 flex justify-end gap-2">
          <Button
            variant={'outline'}
            className={'border-neutral/20'}
            color="neutral"
            onClick={() => onOpenChange(false)}
          >
            {t.markLoadAsReviewed.discard}
          </Button>
          <Button
            onClick={handleMarkAsReviewed}
            isDisabled={!allowSubmitting}
            isLoading={isMarkingAsReviewed}
          >
            {t.markLoadAsReviewed.markAsReviewed}
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export type MarkLoadAsReviewedButtonProps = { loadId?: string };

export const MarkLoadAsReviewedButton = ({
  loadId,
}: MarkLoadAsReviewedButtonProps) => {
  const t = useTranslations();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [showPopover, setShowPopover] = React.useState(false);
  const [didMarkAsReviewed, setDidMarkAsReviewed] = React.useState(false);

  React.useEffect(() => {
    setDidMarkAsReviewed(false);
  }, [loadId]);

  if (didMarkAsReviewed || !loadId) return null;

  return (
    <div className="@MarkLoadAsReviewedButton contents">
      <Button ref={buttonRef} onClick={() => setShowPopover((open) => !open)}>
        {t.markLoadAsReviewed.markAsReviewed}
      </Button>
      <Popover
        isOpen={showPopover}
        onOpenChange={(open) => setShowPopover(open)}
        triggerRef={buttonRef}
        placement="bottom right"
      >
        <MarkLoadAsReviewedPopover
          triggerRef={buttonRef}
          loadId={loadId}
          isOpen={showPopover}
          onOpenChange={setShowPopover}
          onSuccess={() => {
            setDidMarkAsReviewed(true);
          }}
        />
      </Popover>
    </div>
  );
};
